import React, { lazy, Suspense, useRef, useState } from 'react';
import { X, MapPin, Phone, Clock, Star, Tag, LayoutTemplate, Link as LinkIcon, ExternalLink, Edit, Palette, Image as ImageIcon, Check, Loader2, AlertCircle, Lock, ArrowRight, Monitor, Copy, CheckCircle2, Code, Save, Key, Download } from 'lucide-react';
import type { VersaoSite } from '@nyroh/contracts';
import { ClientEntity } from '../types';
import { db } from '../services/db';
import { toast } from 'sonner';
import { exportClientForCPanel } from '../utils/zipHandler';
import { hostPublicoDoSite, urlPublicaDoSite } from '../lib/site-publico';
import { urlDaImagem } from '../lib/api';
import { VersoesDoSite } from './VersoesDoSite';
import type { EditorDeCodigoRef } from './editor/EditorDeCodigo';

/*
  CodeMirror entra por `lazy`, e nao no bundle principal.

  Medido: com ele estatico o bundle do painel saltou de 3,19 MB para 3,79 MB
  (+205 KB comprimido). Isso e peso que TODA pessoa que abre o painel baixa —
  inclusive quem so vai ver a lista de projetos — para uma tela que abre em
  poucos cliques por dia. Com o `lazy`, os 600 KB so descem quando alguem clica
  em "Codigo Fonte".
*/
const EditorDeCodigo = lazy(() =>
  import('./editor/EditorDeCodigo').then((m) => ({ default: m.EditorDeCodigo })),
);
import { BibliotecaDeImagens } from './BibliotecaDeImagens';
import { GaleriaDeTemplates } from './GaleriaDeTemplates';
import { CredenciaisDoCliente } from './admin/CredenciaisDoCliente';

interface ClientModalProps {
  client: ClientEntity;
  coverImage?: string;
  onClose: () => void;
  onPreview: (client: ClientEntity) => void;
  onVisualEdit: (client: ClientEntity) => void;
  onClientUpdate?: () => void;
}

export const ClientModal: React.FC<ClientModalProps> = ({ client, coverImage, onClose, onPreview, onVisualEdit, onClientUpdate }) => {
  const { data } = client;
  const [isEditingSlug, setIsEditingSlug] = useState(false);
  const [newSlug, setNewSlug] = useState(client.slug);
  const [isSavingSlug, setIsSavingSlug] = useState(false);
  const [slugError, setSlugError] = useState<string | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);

  // Code Editor State
  const [showCodeEditor, setShowCodeEditor] = useState(false);
  const [codeContent, setCodeContent] = useState(client.htmlContent);
  const [isSavingCode, setIsSavingCode] = useState(false);
  /**
   * De onde veio o que está no editor — e é isso que autoriza salvar.
   *
   * `null` enquanto carrega. `falha` significa "não consegui LER o que está no
   * ar", e nesse estado salvar é proibido: ver a explicação em `abrirCodigo`.
   */
  const [origemDoCodigo, setOrigemDoCodigo] = useState<
    'publicado' | 'rascunho' | 'sem-site' | 'falha' | null
  >(null);
  const [motivoDaFalha, setMotivoDaFalha] = useState<string | null>(null);
  const [specTipado, setSpecTipado] = useState(false);
  /** Biblioteca aberta por cima do editor, para inserir `<img>` no cursor. */
  const [imagensAbertas, setImagensAbertas] = useState(false);
  /** Galeria de modelos, aberta a partir do painel do projeto. */
  const [galeriaAberta, setGaleriaAberta] = useState(false);
  const editorRef = useRef<EditorDeCodigoRef>(null);
  /**
   * Qual versao o editor esta mostrando.
   *
   * `null` significa "a que esta no ar". Ao abrir o codigo de uma versao ANTIGA
   * pelo historico, guardamos qual e — porque salvar por cima dela nao a
   * substitui: cria uma versao nova a partir do que esta na tela. Sem esse
   * aviso, "editei a versao antiga" vira uma expectativa errada silenciosa.
   */
  const [versaoNoEditor, setVersaoNoEditor] = useState<VersaoSite | null>(null);
  
  const displaySlug = isEditingSlug ? newSlug : client.slug;
  /**
   * O endereco onde o site responde de verdade.
   *
   * Era `window.location.origin + '#/site/' + slug` — um link no dominio do
   * PAINEL, servido por uma rota `acesso: 'operador'`. Rotulado como "Link
   * Público", devolvia 401 para qualquer visitante.
   *
   * Enquanto o slug esta sendo editado, mostra a previa do endereco NOVO: e o
   * que a pessoa precisa ver para decidir se o slug esta bom.
   */
  const siteLink = isEditingSlug
    ? urlPublicaDoSite({ slug: displaySlug, customDomain: client.customDomain, urlPublica: null })
    : urlPublicaDoSite(client);
  const siteHost = isEditingSlug
    ? hostPublicoDoSite({ slug: displaySlug, customDomain: client.customDomain, urlPublica: null })
    : hostPublicoDoSite(client);

  const handleSaveSlug = async () => {
     if (newSlug === client.slug) {
         setIsEditingSlug(false);
         return;
     }
     
     setIsSavingSlug(true);
     setSlugError(null);
     
     const result = await db.updateClientSlug(client.id, newSlug);
     
     if (result.success) {
         setIsEditingSlug(false);
         if (onClientUpdate) onClientUpdate();
     } else {
         setSlugError(result.error || "Erro desconhecido");
     }
     
     setIsSavingSlug(false);
  };

  /**
   * ==========================================================================
   * ABRIR O CÓDIGO-FONTE — carregando o que está NO AR
   * ==========================================================================
   *
   * O BUG QUE ISTO FECHA
   *
   * `codeContent` nascia de `useState(client.htmlContent)` e nunca era
   * ressincronizado — este componente não tinha um `useEffect` sequer. E o
   * `client` que chega aqui vem da LISTAGEM, onde `htmlContent` é `''` de
   * propósito: listagem não arrasta artefato de 12 KB por linha
   * (`db.ts` na busca de clientes, `mapeadores.ts` no `htmlContent = ''`).
   *
   * O resultado é que o editor abria VAZIO para um site publicado, e a faixa
   * âmbar dizia "este site nunca foi publicado" — culpando a coisa errada.
   *
   * E o estrago não parava aí: salvar com o editor vazio chamava
   * `db.updateClientSite` com string vazia, que publica um documento vazio por
   * cima do site que está no ar. O caminho para apagar um site em produção
   * eram dois cliques, sem nenhum aviso.
   *
   * POR QUE `getSiteHtmlParaEditar` E NÃO `client.htmlContent`
   *
   * Ela já existia, e já devolve ESTADO NOMEADO: `publicado`, `rascunho`,
   * `sem-site` ou `falha`. `buscarHtmlPublicado`, que alimenta
   * `client.htmlContent`, devolve `''` tanto para "não há site" quanto para "o
   * R2 não respondeu" — e essas duas coisas pedem reações opostas. Distinguir
   * as duas é o que permite PROIBIR salvar quando a leitura falhou.
   */
  const abrirCodigo = async () => {
    setShowCodeEditor(true);
    setOrigemDoCodigo(null);
    setMotivoDaFalha(null);
    setSpecTipado(false);

    const r = await db.getSiteHtmlParaEditar(client);
    switch (r.origem) {
      case 'publicado':
      case 'rascunho':
        setCodeContent(r.html);
        setOrigemDoCodigo(r.origem);
        setSpecTipado(r.specTipado === true);
        break;
      case 'sem-site':
        // Não há nada para sobrescrever: escrever aqui cria a primeira versão.
        setCodeContent('');
        setOrigemDoCodigo('sem-site');
        break;
      case 'falha':
        setCodeContent('');
        setOrigemDoCodigo('falha');
        setMotivoDaFalha(r.motivo);
        break;
    }
  };

  /**
   * Salvar é proibido em dois estados, e os dois são o mesmo risco: publicar
   * por cima do que está no ar sem ter conseguido ler o que está no ar.
   */
  const podeSalvar =
    origemDoCodigo !== null && origemDoCodigo !== 'falha' && !isSavingCode;

  const handleSaveCode = async () => {
     if (origemDoCodigo === 'falha') return;

     /*
       Publicar vazio por cima de um site publicado é quase sempre engano.

       Não é impossível querer — esvaziar e recomeçar existe — mas com um clique
       e sem pergunta, o caminho mais provável para chegar aqui é o editor ter
       aberto vazio por acidente, que foi exatamente o bug que `abrirCodigo`
       fecha. A confirmação custa um segundo e evita apagar um site em produção.
     */
     if (!codeContent.trim() && origemDoCodigo === 'publicado') {
        const ok = window.confirm(
          'O editor está vazio e este site TEM uma versão no ar.\n\n' +
            'Salvar assim publica uma página em branco por cima dela. Tem certeza?',
        );
        if (!ok) return;
     }

     setIsSavingCode(true);
     try {
        const { mudou } = await db.updateClientSite(client.slug, codeContent);
        client.htmlContent = codeContent; // Update local ref
        setVersaoNoEditor(null);
        setShowCodeEditor(false);
        setOrigemDoCodigo(null);
        // Sem `mudou`, dizer "publicado" quando o conteudo era identico manda a
        // pessoa procurar no historico uma versao que nunca foi criada.
        toast.success(
          mudou
            ? 'Código publicado. A versão anterior continua no histórico.'
            : 'Nada mudou: o conteúdo é idêntico ao que já está no ar, então nenhuma versão nova foi criada.',
        );
        if (onClientUpdate) onClientUpdate();
     } catch (err) {
        console.error('[painel] falha ao salvar o código:', err);
        toast.error(err instanceof Error ? err.message : 'Erro ao salvar código.');
     } finally {
        setIsSavingCode(false);
     }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (err) {
      console.error('Failed to copy', err);
    }
  };

  const [isExporting, setIsExporting] = useState(false);
  const handleDownloadZip = async () => {
    setIsExporting(true);
    try {
      await exportClientForCPanel(client);
      toast.success('Download do ZIP (cPanel/Host) iniciado!');
    } catch (err) {
      console.error(err);
      toast.error('Erro ao gerar o ZIP.');
    } finally {
      setIsExporting(false);
    }
  };

  if (showCodeEditor) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-[#1E1E1E] font-sans animate-in fade-in duration-200">
        <div className="flex items-center justify-between px-6 py-4 bg-[#252526] border-b border-gray-800 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-md bg-blue-500/10 flex items-center justify-center text-blue-400 border border-blue-500/20">
              <Code size={16} />
            </div>
            <div>
              <h2 className="text-sm font-medium text-gray-200">index.html</h2>
              <p className="text-[10px] text-gray-500 font-mono">
                {client.slug} ·{' '}
                {versaoNoEditor
                  ? `versão de ${new Date(versaoNoEditor.criadoEm).toLocaleString('pt-BR')}`
                  : 'versão no ar'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden lg:inline text-[10px] text-gray-500 font-mono mr-1">
              Ctrl+F busca · Ctrl+Espaço sugestões · Tab indenta
            </span>
            <button
              onClick={() => setImagensAbertas(true)}
              className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-gray-300 hover:text-white hover:bg-white/10 rounded-md transition-colors"
            >
              <ImageIcon size={14} />
              Imagens
            </button>
            <button 
              onClick={() => {
                setCodeContent(client.htmlContent); // reset
                setVersaoNoEditor(null);
                setShowCodeEditor(false);
              }} 
              className="px-4 py-2 text-xs font-medium text-gray-400 hover:text-white transition-colors"
            >
              Cancelar
            </button>
            <button 
              onClick={handleSaveCode} 
              disabled={!podeSalvar} 
              title={
                origemDoCodigo === 'falha'
                  ? 'Bloqueado: não consegui ler o site que está no ar'
                  : undefined
              } 
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-xs font-medium transition-colors disabled:opacity-50"
            >
              {isSavingCode ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Salvar Código
            </button>
          </div>
        </div>
        {/*
          Um textarea vazio nao diz por que esta vazio. E ele fica vazio por um
          motivo concreto e corrigivel: ou o site nunca foi publicado (nao ha
          artefato no R2), ou o painel nao conseguiu ler o artefato do CDN. Sem
          esta faixa, o sintoma vira "o código sumiu".
        */}
        {/*
          Uma faixa por ESTADO, e não uma faixa genérica para "está vazio".

          A versão anterior dizia "ou nunca foi publicado, ou o CDN falhou" —
          ela não sabia qual, porque `client.htmlContent` devolve '' para os
          dois casos. Agora `abrirCodigo` sabe, e cada caso pede uma reação
          diferente: um convida a escrever, o outro proíbe salvar.
        */}
        {origemDoCodigo === null && (
          <div className="flex items-center gap-2 border-b border-white/10 bg-white/5 px-6 py-3 text-xs text-gray-400">
            <Loader2 size={14} className="animate-spin shrink-0" />
            Carregando o que está no ar…
          </div>
        )}

        {origemDoCodigo === 'falha' && (
          <div className="flex items-start gap-2 bg-red-500/10 border-b border-red-500/30 text-red-200 px-6 py-3 text-xs leading-relaxed">
            <AlertCircle size={14} className="shrink-0 mt-0.5" />
            <span>
              <b>Não consegui ler o site que está no ar.</b> Salvar está bloqueado de propósito:
              publicar a partir daqui gravaria uma página em branco por cima da que está
              servindo. Feche, confira a conexão com o R2/CDN e abra de novo.
              {motivoDaFalha && (
                <> Detalhe: <code className="font-mono">{motivoDaFalha}</code></>
              )}
            </span>
          </div>
        )}

        {origemDoCodigo === 'sem-site' && (
          <div className="flex items-start gap-2 bg-amber-500/10 border-b border-amber-500/30 text-amber-200 px-6 py-3 text-xs leading-relaxed">
            <AlertCircle size={14} className="shrink-0 mt-0.5" />
            <span>
              <b>Este cliente ainda não tem site.</b> Não há nada para sobrescrever: escrever
              aqui e salvar cria o site e publica a primeira versão. Se preferir começar de um
              modelo pronto, use <b>Modelos</b> no painel do projeto.
            </span>
          </div>
        )}

        {specTipado && (
          <div className="flex items-start gap-2 bg-blue-500/10 border-b border-blue-500/30 text-blue-200 px-6 py-3 text-xs leading-relaxed">
            <AlertCircle size={14} className="shrink-0 mt-0.5" />
            <span>
              Este site é um <b>modelo com seções tipadas</b>, e o que você vê é ele renderizado.
              Salvar por aqui <b>achata</b> o modelo em HTML solto: as seções somem e melhorar o
              modelo no contrato deixa de regenerar este site. Para mexer no conteúdo mantendo o
              modelo, use <b>Modelos</b> em vez deste editor.
            </span>
          </div>
        )}
        {versaoNoEditor && (
          <div className="flex items-start gap-2 bg-blue-500/10 border-b border-blue-500/30 text-blue-200 px-6 py-3 text-xs leading-relaxed">
            <AlertCircle size={14} className="shrink-0 mt-0.5" />
            <span>
              Você abriu uma versão <b>antiga</b>. Salvar aqui <b>não</b> a substitui: cria uma
              versão nova com este conteúdo e a coloca no ar. Para voltar à antiga sem gerar
              outra, use <b>Colocar no ar</b> no histórico de versões.
            </span>
          </div>
        )}
        <div className="flex-1 min-h-0 overflow-hidden">
          <Suspense
            fallback={
              <div className="flex h-full items-center justify-center gap-2 text-xs text-gray-500">
                <Loader2 size={14} className="animate-spin" />
                Carregando o editor…
              </div>
            }
          >
            <EditorDeCodigo
              ref={editorRef}
              valor={codeContent}
              aoMudar={setCodeContent}
              placeholder="<!doctype html>…"
              className="h-full"
            />
          </Suspense>
        </div>

        {/*
          A biblioteca por cima do editor.

          Inserir a imagem no CURSOR, e nao copiar a URL para a area de
          transferencia: o que a pessoa quer e a tag pronta no lugar certo. A
          URL vem de `urlDaImagem`, que resolve a chave contra o CDN — colar a
          chave crua daria um `src` quebrado.
        */}
        {imagensAbertas && (
          <BibliotecaDeImagens
            clienteId={client.id}
            campoAlvo="codigo"
            aoEscolher={(chaveR2) => {
              editorRef.current?.inserirNoCursor(
                `<img src="${urlDaImagem(chaveR2)}" alt="" loading="lazy">`,
              );
              setImagensAbertas(false);
              toast.success('Imagem inserida no cursor.');
            }}
            aoFechar={() => setImagensAbertas(false)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/20 backdrop-blur-sm p-4 sm:p-6 overflow-y-auto font-sans">
      <div className="bg-white w-full max-w-5xl rounded-xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col max-h-[90vh] animate-in fade-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="bg-white border-b border-gray-100 p-6 md:px-8 md:py-6 flex justify-between items-start sticky top-0 z-10 shrink-0">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider">
                {data.businessStatus}
              </span>
              <div className="flex items-center gap-1 text-gray-500 text-xs">
                <Star size={12} className="fill-current text-gray-400" />
                <span className="font-medium">{data.rating}</span>
                <span>({data.reviewCount} reviews)</span>
              </div>
            </div>
            <h2 className="text-2xl font-semibold text-gray-900 tracking-tight mb-2">{data.name}</h2>
            <div className="flex flex-wrap gap-4 text-gray-500 text-sm">
              <div className="flex items-center gap-1.5">
                <MapPin size={14} className="text-gray-400" />
                {data.address}
              </div>
              <div className="flex items-center gap-1.5">
                <Phone size={14} className="text-gray-400" />
                {data.phoneNumber}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-black hover:bg-gray-100 rounded-md transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Content Scrollable Area */}
        <div className="overflow-y-auto p-6 md:p-8 bg-[#FAFAFA] flex-1">
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Left Column: Key Info */}
            <div className="lg:col-span-2 space-y-8">
              
              {/* Cover Image Preview (If Available) */}
              {coverImage && (
                <div className="bg-white p-2 rounded-xl border border-gray-200 shadow-sm">
                   <div className="flex items-center gap-2 mb-2 px-2 pt-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                      <ImageIcon size={14} /> Capa do Projeto
                   </div>
                   <div className="aspect-video w-full overflow-hidden rounded-lg bg-gray-50 border border-gray-100">
                     <img src={coverImage} alt="Site Cover" className="w-full h-full object-cover object-top" />
                   </div>
                </div>
              )}

              {/* Painel de Controle (Actions & Credentials) */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-5 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <LayoutTemplate className="text-gray-400" size={16} />
                      Painel do Projeto
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5">Gerencie acessos e edição do site.</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button onClick={handleDownloadZip} disabled={isExporting} className="flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-md text-xs font-medium transition-all disabled:opacity-50">
                      {isExporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                      Baixar ZIP
                    </button>
                    <button onClick={() => setGaleriaAberta(true)} className="flex items-center justify-center gap-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 px-3 py-1.5 rounded-md text-xs font-medium transition-all">
                      <LayoutTemplate size={14} className="text-gray-400" />
                      Modelos
                    </button>
                    <button onClick={() => setImagensAbertas(true)} className="flex items-center justify-center gap-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 px-3 py-1.5 rounded-md text-xs font-medium transition-all">
                      <ImageIcon size={14} className="text-gray-400" />
                      Imagens
                    </button>
                    <button onClick={() => void abrirCodigo()} className="flex items-center justify-center gap-2 bg-gray-900 hover:bg-black text-white px-3 py-1.5 rounded-md text-xs font-medium transition-all">
                      <Code size={14} />
                      Código Fonte
                    </button>
                    <button onClick={() => onPreview(client)} className="flex items-center justify-center gap-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 px-3 py-1.5 rounded-md text-xs font-medium transition-all">
                      <Monitor size={14} className="text-gray-400" />
                      Preview
                    </button>
                    <button onClick={() => onVisualEdit(client)} className="flex items-center justify-center gap-2 bg-black hover:bg-gray-900 text-white px-3 py-1.5 rounded-md text-xs font-medium transition-all">
                      <Palette size={14} />
                      Editor Visual
                    </button>
                  </div>
                </div>
                
                <div className="p-6 space-y-6">
                  {/* Link Section */}
                  <div>
                    <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Domínio / Link Público</label>
                    {isEditingSlug ? (
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <input 
                              type="text" 
                              value={newSlug}
                              onChange={(e) => setNewSlug(e.target.value)}
                              className="flex-1 border border-gray-300 focus:border-black focus:ring-1 focus:ring-black rounded-md px-3 py-2 text-sm font-mono text-gray-900 outline-none transition-all"
                              placeholder="nome-do-cliente"
                              autoFocus
                          />
                          <button 
                             onClick={handleSaveSlug}
                             disabled={isSavingSlug}
                             className="bg-black hover:bg-gray-900 text-white px-4 py-2 rounded-md transition-colors flex items-center gap-2 text-sm font-medium"
                          >
                             {isSavingSlug ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                             Salvar
                          </button>
                          <button 
                             onClick={() => { setIsEditingSlug(false); setNewSlug(client.slug); setSlugError(null); }}
                             className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 px-3 py-2 rounded-md transition-colors"
                          >
                             <X size={16} />
                          </button>
                        </div>
                        {slugError && (
                          <span className="text-xs text-red-500 flex items-center gap-1">
                             <AlertCircle size={12} /> {slugError}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-gray-50/50 p-3 rounded-lg border border-gray-200">
                        <div className="flex-1 flex items-center gap-3 min-w-0">
                          <div className="w-8 h-8 bg-white rounded-md border border-gray-200 flex items-center justify-center shrink-0 text-gray-400">
                            <LinkIcon size={14} />
                          </div>
                          <span className="text-gray-700 text-sm font-mono truncate select-all">{siteHost}</span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button 
                            onClick={() => copyToClipboard(siteLink)}
                            className="p-2 text-gray-500 hover:text-black hover:bg-gray-100 rounded-md transition-colors"
                            title="Copiar Link"
                          >
                            {copiedLink ? <CheckCircle2 size={16} className="text-green-600" /> : <Copy size={16} />}
                          </button>
                          <button 
                            onClick={() => setIsEditingSlug(true)}
                            className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 rounded-md transition-colors"
                          >
                            Editar
                          </button>
                          <a 
                            href={siteLink}
                            target="_blank"
                            rel="noreferrer"
                            className="px-3 py-1.5 text-xs font-medium text-white bg-black hover:bg-gray-900 rounded-md transition-colors flex items-center gap-1"
                          >
                            Abrir <ExternalLink size={12} />
                          </a>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Versões publicadas */}
                  {client.siteId ? (
                    <VersoesDoSite
                      siteId={client.siteId}
                      slug={client.slug}
                      aoTrocarVersao={onClientUpdate}
                      aoVerCodigo={(html, versao) => {
                        // Aqui o HTML JA veio carregado da versao escolhida — nao
                        // ha o que buscar. Mas a origem precisa ser declarada,
                        // senao `podeSalvar` continua falso e o botao fica morto.
                        setCodeContent(html);
                        setVersaoNoEditor(versao);
                        setOrigemDoCodigo('publicado');
                        setMotivoDaFalha(null);
                        setSpecTipado(false);
                        setShowCodeEditor(true);
                      }}
                    />
                  ) : (
                    <div className="pt-6 border-t border-gray-100">
                      <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2 block">
                        Versões publicadas
                      </label>
                      <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3 text-xs">
                        <b>Este cliente ainda não tem site.</b> Ele foi criado no CRM, mas nenhum
                        site foi gerado para ele. Abra o <b>Editor Visual</b> e salve: o primeiro
                        salvar cria o site e publica a primeira versão.
                      </div>
                    </div>
                  )}

                  <CredenciaisDoCliente clienteId={client.id} slug={client.slug} />

                </div>
              </div>

              {/* Summary */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <div className="w-1 h-4 bg-black rounded-full"></div>
                  Resumo Executivo
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {data.summary}
                </p>
              </div>

              {/* Blueprints */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <div className="w-1 h-4 bg-black rounded-full"></div>
                  Estratégia & Blueprint
                </h3>
                <div className="grid gap-4">
                  {(data.blueprint || []).map((item, idx) => (
                    <div key={idx} className="bg-white p-5 rounded-xl shadow-sm border border-gray-200 hover:border-gray-300 transition-colors">
                      <h4 className="font-medium text-gray-900 text-sm mb-4">{item.title}</h4>
                      
                      <div className="space-y-4">
                        <div>
                          <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Estratégia UX</span>
                          <p className="text-gray-700 mt-1 text-sm">{item.uxStrategy}</p>
                        </div>

                        <div>
                           <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Descrição</span>
                           <p className="text-gray-700 mt-1 text-sm">{item.description}</p>
                        </div>
                        
                        <div className="bg-gray-50 p-3 rounded-md border border-gray-100">
                          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1">
                            <LayoutTemplate size={12} /> Sugestão Visual
                          </span>
                          <p className="text-gray-600 mt-1 text-xs italic">{item.visualSuggestion}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Photo Analysis */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                 <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                   <div className="w-1 h-4 bg-black rounded-full"></div>
                   Análise Fotográfica
                 </h3>
                 <p className="text-gray-600 text-sm">{data.photoAnalysis}</p>
              </div>

            </div>

            {/* Right Column: Metadata */}
            <div className="space-y-6">
              
              {/* Opening Hours */}
              <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200">
                <div className="flex items-center gap-2 mb-4 text-gray-900">
                  <Clock size={16} className="text-gray-400" />
                  <h3 className="font-medium text-sm">Horários</h3>
                </div>
                <ul className="space-y-2 text-xs text-gray-600">
                  {(() => {
                    let hoursList: string[] = [];
                    const rawHours = data.openingHours;
                    
                    if (!rawHours) {
                      return <li className="text-gray-400 italic">Não informados</li>;
                    }
                    
                    if (typeof rawHours === 'string') {
                      if (rawHours.includes(';')) {
                         hoursList = rawHours.split(';');
                      } else if (rawHours.includes(',')) {
                         hoursList = rawHours.split(',');
                      } else {
                         hoursList = [rawHours];
                      }
                    } else if (Array.isArray(rawHours)) {
                      hoursList = rawHours.map(h => String(h));
                    } else if (typeof rawHours === 'object') {
                      if (Array.isArray(rawHours.weekday_text)) {
                        hoursList = rawHours.weekday_text.map((h: any) => String(h));
                      } else {
                        hoursList = Object.entries(rawHours).map(([k, v]) => `${k}: ${v}`);
                      }
                    } else {
                      hoursList = [String(rawHours)];
                    }

                    if (hoursList.length === 0) {
                      return <li className="text-gray-400 italic">Não informados</li>;
                    }

                    return hoursList.map((h, i) => {
                      const colonIdx = h.indexOf(':');
                      if (colonIdx !== -1) {
                        const day = h.substring(0, colonIdx).trim();
                        const time = h.substring(colonIdx + 1).trim();
                        return (
                          <li key={i} className="flex justify-between border-b border-gray-100 pb-2 last:border-0 last:pb-0">
                            <span className="font-medium capitalize text-gray-700">{day}</span>
                            <span className="text-right text-gray-500">{time}</span>
                          </li>
                        );
                      }
                      return (
                        <li key={i} className="flex justify-between border-b border-gray-100 pb-2 last:border-0 last:pb-0">
                          <span className="font-medium text-gray-700">{h.trim()}</span>
                        </li>
                      );
                    });
                  })()}
                </ul>
              </div>

              {/* Categories & Keywords */}
              <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200">
                <div className="flex items-center gap-2 mb-4 text-gray-900">
                  <Tag size={16} className="text-gray-400" />
                  <h3 className="font-medium text-sm">Nicho & SEO</h3>
                </div>
                
                <div className="mb-5">
                  <p className="text-[10px] text-gray-400 mb-2 uppercase font-semibold">Categorias</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(data.categories || []).map((cat, i) => (
                      <span key={i} className="px-2 py-1 bg-gray-50 text-gray-600 rounded-md text-xs border border-gray-200">
                        {cat}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-[10px] text-gray-400 mb-2 uppercase font-semibold">Keywords</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(data.seoKeywords || []).map((kw, i) => (
                      <span key={i} className="px-2 py-1 bg-gray-50 text-gray-600 rounded-md text-xs border border-gray-200 font-medium">
                        #{kw}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Amenities */}
              <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200">
                <h3 className="font-medium text-sm text-gray-900 mb-3">Comodidades</h3>
                <ul className="space-y-2">
                  {(data.amenities || []).map((item, i) => (
                     <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
                       <Check size={14} className="text-gray-400 shrink-0 mt-0.5" /> 
                       <span>{item}</span>
                     </li>
                  ))}
                </ul>
              </div>
              
              {/* ID Metadata */}
               <div className="p-3 rounded-lg border border-gray-200 text-gray-400 text-[10px] font-mono break-all bg-gray-50/50">
                <div className="mb-1"><span className="text-gray-500 font-semibold">ID:</span> {data.id}</div>
                <div><span className="text-gray-500 font-semibold">Campaign:</span> {data.campaignId}</div>
               </div>

            </div>
          </div>
        </div>

      </div>

      {galeriaAberta && (
        <GaleriaDeTemplates
          client={client}
          aoFechar={() => setGaleriaAberta(false)}
          aoAplicar={() => onClientUpdate?.()}
        />
      )}

      {/*
        A mesma biblioteca do editor, aberta pelo painel do projeto.

        Aqui nao ha cursor para inserir: `campoAlvo={null}` deixa o clique sem
        destino e a tela vira administracao — subir, ver o que ja existe,
        apagar. Dentro do editor de codigo ela abre com destino, e o clique
        insere a tag no lugar do cursor.
      */}
      {imagensAbertas && !showCodeEditor && (
        <BibliotecaDeImagens
          clienteId={client.id}
          campoAlvo={null}
          aoEscolher={() => {}}
          aoFechar={() => setImagensAbertas(false)}
        />
      )}
    </div>
  );
};
